import { getPreferredPhone, useGetAllContacts, useGetContact } from "@api/contactService";
import React, { useContext, useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ObjectShow from "@components/objects/ObjectShow";
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import ObjectColumn from "@components/objects/ObjectColumn";
import { ObjectPrimaryInfo } from "@components/objects/ObjectPrimaryInfo";
import { ObjectRelationship } from "@components/objects/ObjectRelationship";
import Avatar from "@mui/joy/Avatar";
import { ObjectContext } from "@providers/ObjectProvider";
import { ensureDateObject, formatAbsoluteDate, formatDate, formatSeconds, isValidDate } from "@utils/dates";
import ObjectPreview from "@components/objects/ObjectPreview";
import { generateAddress } from "@utils/generateAddress";
import { CardOverflow, List, ListDivider, ListItem, ListItemContent, ListItemDecorator, Table } from "@mui/joy";
import AbstractObjectRelationship from "@components/objects/AbstractObjectRelationship";
import UserAccessCard from "@components/objects/UserAccessCard";
import { formatDistanceToNow, formatDistanceToNowStrict, intervalToDuration } from "date-fns";
import { useGetEvent } from "@api/eventService";
import { getFrequencyName, useGetDonation } from "@api/donationService";
import { CheckPermission } from "@components/CheckPermission";
import { importStatuses, importTypes, useGetImport, useRetryImport } from "@api/importService";
import { useAuth } from "@providers/AuthProvider";
import pluralize from 'pluralize';
import { authenticatedApi } from "api";
import toast from "react-hot-toast";

function LastUpdatedText({date}) {
    const [dateText, setDateText] = useState('');
    useEffect(() => {
        const intervalId = setInterval(() => {
            const textStrict = formatDistanceToNowStrict(date, {addSuffix: true});
            // const text = formatDistanceToNow(date, {includeSeconds: 'true', addSuffix: true});
            setDateText(textStrict);
        }, 500);

        return () => clearInterval(intervalId);
    }, [date])

    return <>{dateText}</>;
};

function RetryImport(props) {
    const data = useContext(ObjectContext) ?? {};
    const {user, can} = useAuth();

    const { mutateAsync, isLoading } = useRetryImport({id: data.id});

    const doRetry = () => {
        const performRetry = mutateAsync();

        toast.promise(performRetry, {
            loading: `Retrying...`,
            success: `The import is being retried.`,
            error: (err) => `There was an error while retrying.`,
        });
    };

    if(data?.status !== 'failed') {
        return null;
    }

    return (
        <CheckPermission permissions="retry imports">
            <Tooltip title="Retry">
                <IconButton
                    size="sm"
                    color="neutral"
                    variant="outlined"
                    sx={{
                        bgcolor: 'background.surface',
                    }}
                    onClick={()=>doRetry()}
                    disabled={isLoading}
                    {...props}
                >
                    <ReplayRoundedIcon/>
                </IconButton>
            </Tooltip>
        </CheckPermission>
    );
}


const dataToDisplay = [
    {
        label: 'Type',
        content: (row) => <>{importTypes?.[row.type] ?? 'N/A'}</>
    },
    {
        label: 'Status',
        content: (row) => {
            console.log(row);
            console.log(row.query_dataUpdatedAt);

            return (<>
                {importStatuses?.[row.status] ?? 'N/A'}{' '}
                {row.status === 'processing' && row.hasOwnProperty('query_dataUpdatedAt') === true && (<>
                    (Updated <LastUpdatedText date={row.query_dataUpdatedAt}/>)
                </>)}


                {row.status === 'failed' && row?.statistics?.attempts > 0 && (<>
                    ({row?.statistics?.attempts} {pluralize('attempt', row?.statistics?.attempts)})
                </>)}
            </>)}
    },
    {
        label: 'Failed Reason',
        conditional: (row) => row?.status === 'failed',
        content: (row) => <>{row?.statistics?.failed_reason ?? 'N/A'}</>
    },

    {
        label: 'File',
        content: (row) => {
            return (<>
                {row.files.length > 0 && (
                    <a href={row.files[row.files.length - 1].url} target="_blank"  sx={{display: 'block'}}>{row.files[row.files.length - 1].name}</a>
                )}
            </>)
        },
    },
    {
        label: 'Date',
        content: (row) => <>{formatDate(row.created_at)}</>
    },
];


const reportToDisplay = [
    {
        label: false,
        conditional: (object) => !object?.statistics,
        content: (object) => <>
            {(object.status != 'queued' && object.status != 'processing') && (
                <em>A report is not available for this import.</em>
            )}
            {(object.status == 'queued' || object.status == 'processing') && (
                <em>A report will be available when the import is complete.</em>
            )}
        </>
    },
    {
        label: 'Duration',
        conditional: (object) => !!object?.statistics?.duration,
        content: (object) => <>{formatSeconds(object.statistics.duration)}</>
    },
    {
        label: 'Contacts',
        conditional: (object) => !!object?.statistics?.imported?.contacts,
        content: (object) => <>
            {object.statistics.imported.contacts.length}
            {!!object.statistics.skipped.contacts.length && <> ({object.statistics.skipped.contacts.length} skipped)</>}
        </>
    },
    {
        label: 'Businesses',
        conditional: (object) => !!object?.statistics?.imported?.businesses,
        content: (object) => <>
            {object.statistics.imported.businesses.length}
            {!!object.statistics.skipped.businesses.length && <> ({object.statistics.skipped.businesses.length} skipped)</>}
        </>
    },
    {
        label: 'Properties',
        conditional: (object) => !!object?.statistics?.imported?.properties,
        content: (object) => <>
            {object.statistics.imported.properties.length}
            {!!object.statistics.skipped.properties.length && <> ({object.statistics.skipped.properties.length} skipped)</>}
        </>
    },
    {
        label: 'Work Plan',
        conditional: (object) => !!object?.statistics?.imported?.workplan,
        content: (object) => <>Yes</>
    },
    {
        label: 'Failed',
        conditional: (object) => !!Object.keys(object?.statistics?.failed ?? {}).length,
        content: (object) => <>{Object.keys(object?.statistics.failed ?? {}).length}</>
    },
];

export default function ImportShow() {
    return (
        <ObjectShow
            query={useGetImport}
            type={'import'}
            permissionType={'noop'}
            title={'Import'}
            // subtitleKey={'name'}
            color={'default'}
            headerActions={<RetryImport/>}
        >
            <ObjectColumn>
                <ObjectPrimaryInfo
                    title={'Import Info'}
                    hideTitle={false}
                    tint={'default'}
                    fields={dataToDisplay}
                    // cardHeader={<CardHeader/>}
                />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectPrimaryInfo
                    title={'Report Info'}
                    hideTitle={false}
                    tint={'default'}
                    fields={reportToDisplay}
                    // cardHeader={<CardHeader/>}
                />

                {/* skipped & failed */}
            </ObjectColumn>
        </ObjectShow>
    )
}
